import { XIcon } from "lucide-react";
import type * as React from "react";
import { Drawer as VaulDrawer } from "vaul";

import { useDrawerSmUp } from "@/web/lib/drawerLayout";
import { cn } from "@/web/lib/styling";

/** Bottom sheet: centered column; right panel: full width of the narrow sheet. */
const DRAWER_INNER_WIDTH_CLASS = cn("mx-auto w-full max-w-md", "sm:mx-0 sm:max-w-none");

function Drawer({
  direction: directionProp,
  autoFocus = true,
  ...rest
}: React.ComponentProps<typeof VaulDrawer.Root>) {
  const smUp = useDrawerSmUp();
  const direction = directionProp ?? (smUp ? "right" : "bottom");
  // Vaul defaults autoFocus false and calls preventDefault on Radix onOpenAutoFocus, leaving focus on a non-trigger control outside the dialog → aria-hidden conflicts. This is Radix modal focus management, not HTML autofocus.
  // oxlint-disable-next-line jsx-a11y/no-autofocus -- programmatic initial focus into modal content (see above)
  return (
    <VaulDrawer.Root
      data-slot="withus-drawer"
      autoFocus={autoFocus}
      {...rest}
      direction={direction}
    />
  );
}

function DrawerTrigger({ ...props }: React.ComponentProps<typeof VaulDrawer.Trigger>) {
  return <VaulDrawer.Trigger data-slot="withus-drawer-trigger" {...props} />;
}

function DrawerPortal({ ...props }: React.ComponentProps<typeof VaulDrawer.Portal>) {
  return <VaulDrawer.Portal data-slot="withus-drawer-portal" {...props} />;
}

function DrawerClose({ className, ...props }: React.ComponentProps<typeof VaulDrawer.Close>) {
  return (
    <VaulDrawer.Close
      data-slot="withus-drawer-close"
      className={cn("opacity-70 transition-opacity hover:opacity-100", className)}
      {...props}
    />
  );
}

function DrawerOverlay({ className, ...props }: React.ComponentProps<typeof VaulDrawer.Overlay>) {
  return (
    <VaulDrawer.Overlay
      data-slot="withus-drawer-overlay"
      className={cn("fixed inset-0 z-50 bg-overlay-35", className)}
      {...props}
    />
  );
}

export type DrawerContentProps = React.ComponentProps<typeof VaulDrawer.Content> & {
  /**
   * Set true when this drawer renders `DrawerDescription` so `aria-describedby` stays linked.
   * When false (default), Radix missing-description warning is suppressed for title-only drawers.
   */
  hasAriaDescription?: boolean;
};

function DrawerContent({
  className,
  children,
  hasAriaDescription = false,
  ...props
}: DrawerContentProps) {
  const smUp = useDrawerSmUp();
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <VaulDrawer.Content
        data-slot="withus-drawer-content"
        className={cn(
          "fixed z-50 flex min-h-0 min-w-0 flex-col overscroll-contain bg-background outline-none",
          smUp
            ? "inset-y-0 right-0 w-[min(28rem,calc(100dvw-1rem))] border-l border-withus-dark-blue/15 shadow-[-12px_0_40px_rgba(0,0,0,0.2)]"
            : "inset-x-0 bottom-0 mt-24 h-[90dvh] shadow-withus-default",
          className,
        )}
        {...(!hasAriaDescription ? { "aria-describedby": undefined } : {})}
        {...props}
      >
        {children}
      </VaulDrawer.Content>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, children, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="withus-drawer-header"
      className={cn(
        "flex items-center justify-between gap-4 px-5 py-5",
        DRAWER_INNER_WIDTH_CLASS,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function DrawerTitle({ className, ...props }: React.ComponentProps<typeof VaulDrawer.Title>) {
  return (
    <VaulDrawer.Title
      data-slot="withus-drawer-title"
      className={cn("text-sm font-semibold text-foreground", className)}
      {...props}
    />
  );
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof VaulDrawer.Description>) {
  return (
    <VaulDrawer.Description
      data-slot="withus-drawer-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

function DrawerCloseIcon({ className, ...props }: React.ComponentProps<typeof VaulDrawer.Close>) {
  return (
    <DrawerClose
      className={cn("inline-flex items-center justify-center", className)}
      aria-label="Close"
      {...props}
    >
      <XIcon className="size-5" />
    </DrawerClose>
  );
}

function DrawerBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="withus-drawer-body"
      className={cn(
        "flex-1 overflow-y-auto overscroll-contain px-5 pb-5",
        DRAWER_INNER_WIDTH_CLASS,
        className,
      )}
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="withus-drawer-footer"
      className={cn("flex flex-col gap-2 px-5 py-5", DRAWER_INNER_WIDTH_CLASS, className)}
      {...props}
    />
  );
}

export {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerCloseIcon,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
